import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useId } from "react";
import { useT } from "./i18n";

export function Button({
  variant = "primary",
  busy,
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  busy?: boolean;
}) {
  const styles = {
    primary: "bg-brand-700 text-white hover:bg-brand-800 disabled:bg-brand-700/60",
    secondary: "bg-white text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50",
    ghost: "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
    danger: "text-red-700 hover:bg-red-50 hover:text-red-800",
  }[variant];
  return (
    <button
      type="submit"
      {...props}
      disabled={busy || props.disabled}
      aria-busy={busy || undefined}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed ${styles} ${className}`}
    >
      {busy ? <Spinner /> : null}
      {children}
    </button>
  );
}

export function Field({
  label,
  error,
  hint,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string; hint?: string }) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <input
        id={id}
        {...props}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`block w-full rounded-lg border bg-white px-3 py-2.5 text-sm shadow-xs placeholder:text-slate-400 focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 focus:outline-none ${
          error ? "border-red-400" : "border-slate-300"
        }`}
      />
      {error ? (
        <p id={`${id}-error`} className="text-sm text-red-700">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-sm text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function Alert({ tone = "error", children }: { tone?: "error" | "success" | "info"; children: ReactNode }) {
  const styles = {
    error: "bg-red-50 text-red-800 ring-red-200",
    success: "bg-brand-50 text-brand-900 ring-brand-100",
    info: "bg-aqua-50 text-aqua-900 ring-aqua-200",
  }[tone];
  return (
    <div role={tone === "error" ? "alert" : "status"} className={`rounded-lg px-4 py-3 text-sm ring-1 ${styles}`}>
      {children}
    </div>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200 ${className}`}>{children}</div>;
}

export function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
    />
  );
}

export function FullPageSpinner() {
  const { t } = useT();
  return (
    <div className="flex min-h-screen items-center justify-center text-brand-700" role="status">
      <Spinner />
      <span className="sr-only">{t("common.loading")}</span>
    </div>
  );
}

const controlClass = (error?: string) =>
  `block w-full rounded-lg border bg-white px-3 py-2.5 text-sm shadow-xs focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 focus:outline-none ${
    error ? "border-red-400" : "border-slate-300"
  }`;

export function Select({
  label,
  error,
  hint,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; error?: string; hint?: string }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <select id={id} {...props} aria-invalid={error ? true : undefined} className={controlClass(error)}>
        {children}
      </select>
      {error ? (
        <p className="text-sm text-red-700">{error}</p>
      ) : hint ? (
        <p className="text-sm text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

export function Textarea({
  label,
  error,
  hint,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; error?: string; hint?: string }) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <textarea
        id={id}
        rows={3}
        {...props}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={controlClass(error)}
      />
      {error ? (
        <p id={`${id}-error`} className="text-sm text-red-700">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-sm text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function Badge({ tone, children }: { tone: "green" | "red" | "gray" | "amber"; children: ReactNode }) {
  const styles = {
    green: "bg-brand-50 text-brand-800 ring-brand-100",
    red: "bg-red-50 text-red-800 ring-red-200",
    gray: "bg-slate-100 text-slate-700 ring-slate-200",
    amber: "bg-amber-50 text-amber-800 ring-amber-200",
  }[tone];
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${styles}`}>{children}</span>
  );
}

export function PageHeader({ title, subtitle, children }: { title: string; subtitle?: string; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold">{title}</h1>
        {subtitle ? <p className="mt-1 text-slate-600">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

/** The dolphin mark and word mark. `tone="light"` is for dark (navy) backgrounds. */
export function Logo({ tone = "dark", className = "" }: { tone?: "dark" | "light"; className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-2 text-lg font-bold tracking-tight whitespace-nowrap ${
        tone === "light" ? "text-white" : "text-slate-900"
      } ${className}`}
    >
      <img src="/logo-mark.png" alt="" width={36} height={36} className="h-9 w-9" />
      <span>
        Dolphin <span className={tone === "light" ? "text-aqua-300" : "text-brand-700"}>Booking</span>
      </span>
    </span>
  );
}
